import { useEffect, useRef, useState, type ReactNode } from 'react';

/** A pop-up in the prototype's style. */
export function Modal({ title, children, onClose, wide }: { title: string; children: ReactNode; onClose?: () => void; wide?: boolean }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="modalbg" role="presentation">
      <div className={'modal' + (wide ? ' widemodal' : '')} role="dialog" aria-modal="true" aria-label={title}>
        <div style={{ fontSize: 18, fontWeight: 600 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

export interface ConfirmSpec { title: string; text: string; ok: string; run: () => Promise<void> | void }

/** "Are you sure?" pop-up, like the prototype's confirm. */
export function Confirm({ spec, onClose }: { spec: ConfirmSpec; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={spec.title} onClose={onClose}>
      <div style={{ fontSize: 14, lineHeight: 1.5, color: '#17212B' }}>{spec.text}</div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn2" onClick={onClose}>Cancel</button>
        <button className="tbtn primary" style={{ minWidth: 90 }} disabled={busy}
          onClick={async () => { setBusy(true); try { await spec.run(); onClose(); } finally { setBusy(false); } }}>{spec.ok}</button>
      </div>
    </Modal>
  );
}

/** The little "i" with a tooltip. */
export function Tip({ text, right, label }: { text: ReactNode; right?: boolean; label?: string }) {
  return (
    <span className={'tip' + (right ? ' tipR' : '')} tabIndex={0} role="button" aria-label={label ?? 'More information'}>
      <span aria-hidden="true">i</span><span className="tipbox" role="tooltip">{text}</span>
    </span>
  );
}

/** A short message at the bottom of the screen that goes away by itself. */
export function useToast(): [ReactNode, (msg: string) => void] {
  const [msg, setMsg] = useState('');
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(''), 3500);
    return () => clearTimeout(t);
  }, [msg]);
  return [msg ? <div className="toast" role="status">{msg}</div> : null, setMsg];
}

export const LEVEL_NAMES: Record<number, string> = {
  9: '9 · Manager', 8: '8 · Assistant manager', 7: '7 · Day manager', 6: '6 · Shift lead', 5: '5 · All activities',
  4: '4 · Karts (secondary)', 3: '3 · Laser tag, no karts', 2: '2 · No karts or laser tag', 1: '1 · Redemption, pixel, floor', 0: '0 · Training',
};
export const LEVELS = [9, 8, 7, 6, 5, 4, 3, 2, 1, 0];

/**
 * The tab bar stays at the top of the window while the page scrolls (owner 10/25); the title bar above it scrolls away.
 * Its height goes in --navh so the day board's hour row and the Help contents stick just below it.
 */
export function useStickyNav<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const set = () => document.documentElement.style.setProperty('--navh', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set); ro.observe(el);
    return () => { ro.disconnect(); document.documentElement.style.removeProperty('--navh'); };
  }, []);
  return ref;
}
